import { ModelBase } from "@models/UpsertableBase";
import { DataType } from "@dndModels/Types";

// The labels some rules definitions have after their name, like "Blinded [Condition]" or "Attack [Action]".
export const RULE_TAG_VALUES = ["None","Action","Area of Effect","Attitude","Condition","Hazard"] as const;
export type RuleTag = typeof RULE_TAG_VALUES[number];
export const isRuleTag = (str: string): str is RuleTag => (RULE_TAG_VALUES as readonly string[]).includes(str);

/**
 * A rules definition: a short, self-contained explanation of a game term,
 * like the entries in the Rules Definitions (Rules Glossary) of the 2024 rules.
 * The name and description are the term and its definition.
 * They show in source books through the Rules Definitions special section type,
 * and in the Rules Reference.
 */
export class RuleDefinition extends ModelBase {
  data_type: DataType = "rule_definition";
  rule_tag: RuleTag = "None";
  see_also_ids: string[] = []; // Other RuleDefinitions it refers to.

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.rule_tag = obj.rule_tag && isRuleTag(obj.rule_tag) ? obj.rule_tag : "None";
      this.see_also_ids = obj.see_also_ids ? [...obj.see_also_ids] : [];
    }
  }

  // "Blinded [Condition]", or just the name when it has no tag.
  get tagged_name(): string {
    return this.rule_tag !== "None" ? `${this.name} [${this.rule_tag}]` : this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      rule_tag: this.rule_tag,
      see_also_ids: this.see_also_ids,
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      rule_tag: this.rule_tag,
      see_also_ids: this.see_also_ids,
    };
  }

  copy(copyMe: RuleDefinition, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.rule_tag = copyMe.rule_tag;
    this.see_also_ids = [...copyMe.see_also_ids];
  }

  clone(drop_ids = false): RuleDefinition {
    const new_obj = new RuleDefinition();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
